import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { generateHarmonizationCode } from './harmonization-api';
import { harmonizationMessages as messages } from './messages';

/**
 * Hook for managing single product harmonization suggestions
 * @param {number} productId
 * @param {string} productDescription
 * @param {string} customsDescription
 * @returns {{
 *   suggestions: Array<import('./harmonization-api').HarmonizationSuggestion>,
 *   isLoading: boolean,
 *   generateSuggestions: (workflowGuid: string) => Promise<void>,
 *   clearSuggestions: () => void,
 * }}
 */
export const useHarmonizationSuggestion = (productId, productDescription, customsDescription = '') => {
  const [suggestions, setSuggestions] = useAsyncSafeState([]);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);

  const generateSuggestions = useCallback(
    async (workflowGuid) => {
      setIsLoading(true);

      try {
        const result = await generateHarmonizationCode(productId, productDescription, customsDescription, workflowGuid);
        setSuggestions(result.suggestions);
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.generateError} />,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, productId, productDescription, customsDescription, setSuggestions],
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, [setSuggestions]);

  return {
    suggestions,
    isLoading,
    generateSuggestions,
    clearSuggestions,
  };
};
